import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, User, Phone, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import api from '../utils/api';
import { setToken, setUser } from '../utils/auth';

const RegisterPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    role: 'client',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/auth/register', formData);
      const { user, token } = response.data;

      setToken(token);
      setUser(user);

      toast.success(
        i18n.language === 'ar' ? 'تم إنشاء الحساب بنجاح' : 'Account created successfully'
      );

      // Redirect based on user role
      if (user.role === 'client') {
        navigate('/client/dashboard');
      } else if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'lawyer') {
        navigate('/lawyer');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          (i18n.language === 'ar' ? 'فشل إنشاء الحساب' : 'Registration failed')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden" data-testid="register-page">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-wazin-navy/5 via-gray-50 to-wazin-gold/5 -z-20" />
      
      {/* Watermark - Wazin Logo */}
      <div className="absolute inset-0 flex items-center justify-center -z-10 pointer-events-none">
        <img 
          src="/images/wazin-logo.png" 
          alt="Wazin Watermark" 
          className="w-[500px] md:w-[700px] lg:w-[900px] h-auto object-contain opacity-[0.07] select-none"
          style={{ filter: 'grayscale(10%)' }}
        />
      </div>
      
      <Card className="w-full max-w-md glass shadow-2xl border-2 border-wazin-gold/20 backdrop-blur-xl bg-white/95" data-testid="register-form-card">
        <CardHeader className="text-center space-y-4 pb-6">
          {/* Wazin Icon */}
          <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-wazin-gold to-yellow-600 flex items-center justify-center shadow-lg">
            <img 
              src="/wazin-icon-gold.png" 
              alt="Wazin Icon" 
              className="w-12 h-12 object-contain filter brightness-0 invert"
            />
          </div>
          <div>
            <CardTitle className="text-3xl font-bold text-wazin-navy">
              {t('registerTitle')}
            </CardTitle>
            <CardDescription className="text-lg mt-2 text-gray-600">
              {i18n.language === 'ar'
                ? 'انضم إلى منصة وازن القانونية'
                : 'Join Wazin Legal Platform'
              }
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
            <div className="space-y-2">
              <Label htmlFor="full_name" className="text-base">
                {t('fullName')}
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-secondary" />
                <Input
                  id="full_name"
                  type="text"
                  placeholder={i18n.language === 'ar' ? 'الاسم الكامل' : 'Full Name'}
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="pl-10 input"
                  required
                  data-testid="fullname-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-base">
                {t('email')}
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-secondary" />
                <Input
                  id="email"
                  type="email"
                  placeholder={i18n.language === 'ar' ? 'البريد الإلكتروني' : 'your@email.com'}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10 input"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-base">
                {t('phone')}
              </Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-secondary" />
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+966 5XX XXX XXX"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="pl-10 input"
                  data-testid="phone-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-base">
                {t('password')}
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-secondary" />
                <Input
                  id="password"
                  type="password"
                  placeholder="********"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 input"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-wazin-gold to-yellow-600 hover:from-wazin-gold/90 hover:to-yellow-600/90 text-wazin-navy font-bold text-lg py-6 shadow-lg hover:shadow-xl transition-all duration-300"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('loading')}
                </>
              ) : (
                t('register')
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              {t('alreadyHaveAccount')}{' '}
              <Link to="/login" className="text-wazin-gold font-semibold hover:text-wazin-navy transition-colors" data-testid="login-link">
                {t('login')}
              </Link>
            </p>
          </div>

          <div className="mt-4 text-center">
            <Link to="/" className="text-sm text-gray-500 hover:text-wazin-gold transition-colors" data-testid="back-home-link">
              {i18n.language === 'ar' ? 'العودة للرئيسية' : 'Back to Home'}
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RegisterPage;
